import * as React from "react";

const STORAGE_KEY = "theme-mode";

function getSystemTheme() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function resolveTheme(mode) {
  return mode === "system" ? getSystemTheme() : mode;
}

const ThemeContext = React.createContext(null);

export function ThemeProvider({ children }) {
  const [mode, setMode] = React.useState("light");

  React.useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === "light" || stored === "dark" || stored === "system") {
        setMode(stored);
      }
    } catch {}
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const apply = () => {
      const resolved = resolveTheme(mode);
      document.documentElement.dataset.theme = resolved;
      document.documentElement.style.colorScheme = resolved;
    };

    apply();

    if (mode !== "system") return;

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => apply();

    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [mode]);

  const value = React.useMemo(() => {
    const resolved = resolveTheme(mode);
    return {
      mode,
      theme: resolved,
      setMode: (next) => {
        setMode(next);
        try {
          window.localStorage.setItem(STORAGE_KEY, next);
        } catch {}
      },
    };
  }, [mode]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
